import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Layout from '../components/Layout';
import { customersAPI } from '../services/api';
import { useToast } from '../components/Toast';
import { Search, Eye, RefreshCw, X, ShoppingBag, Heart, MapPin } from 'lucide-react';

// ── Data masking helpers ──────────────────────────────────────────────
function maskEmail(email) {
  if (!email) return '—';
  const [local, domain] = email.split('@');
  if (!domain) return email;
  const [domainName, ...tld] = domain.split('.');
  const m = s => s.length <= 2 ? s : s[0] + '•'.repeat(Math.min(s.length - 2, 6)) + s[s.length - 1];
  return m(local) + '@' + m(domainName) + '.' + tld.join('.');
}
function maskPhone(phone) {
  if (!phone) return '—';
  const p = String(phone);
  if (p.length <= 4) return p;
  return p.slice(0, 2) + '•'.repeat(Math.min(p.length - 4, 6)) + p.slice(-2);
}
function maskId(id) {
  if (!id) return '—';
  const s = String(id);
  if (s.length <= 8) return s;
  return s.slice(0, 4) + '•'.repeat(Math.min(s.length - 8, 20)) + s.slice(-4);
}

function CustomerDetailModal({ customerId, onClose }) {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    customersAPI.getById(customerId)
      .then(r => { setCustomer(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [customerId]);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <span className="modal-title">Customer Profile</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        {loading ? (
          <div className="page-loader"><div className="spinner" /></div>
        ) : !customer ? (
          <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 32 }}>Could not load customer</p>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 16, background: 'var(--bg-hover)', borderRadius: 12, marginBottom: 20 }}>
              <div className="avatar lg">
                {customer.profilePicture
                  ? <img src={customer.profilePicture} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                  : customer.fullName?.[0]}
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{customer.fullName}</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{maskEmail(customer.email)}</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{maskPhone(customer.phone)}</div>
              </div>
            </div>

            {/* Stats row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 20 }}>
              {[
                { icon: ShoppingBag, label: 'Orders', value: customer.totalOrders ?? 0, color: 'var(--primary-dark)' },
                { icon: Heart, label: 'Wishlist', value: customer.wishlistCount ?? 0, color: 'var(--red)' },
                { icon: MapPin, label: 'Addresses', value: customer.savedAddressCount ?? 0, color: 'var(--green)' },
              ].map(({ icon: Icon, label, value, color }) => (
                <div key={label} style={{ background: 'var(--bg-hover)', borderRadius: 10, padding: '12px 14px', textAlign: 'center' }}>
                  <Icon size={18} style={{ color, marginBottom: 6 }} />
                  <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)' }}>{value}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</div>
                </div>
              ))}
            </div>

            <div className="detail-row">
              <span className="detail-label">Customer ID</span>
              <span className="detail-value" style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{maskId(customer.customerId)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Member Since</span>
              <span className="detail-value">
                {customer.memberSince ? new Date(customer.memberSince).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' }) : '—'}
              </span>
            </div>
          </>
        )}
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Mobile Customer Card ──────────────────────────────────────────────
function CustomerCard({ c, onView }) {
  return (
    <button className="cust-card" onClick={() => onView(c.customerId)} type="button">
      <div className="cust-card-top">
        <div className="avatar" style={{ width: 42, height: 42, fontSize: 16, flexShrink: 0 }}>
          {c.profilePicture
            ? <img src={c.profilePicture} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
            : c.fullName?.[0]}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.fullName}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--mono)', marginTop: 2 }}>{maskEmail(c.email)}</div>
        </div>
        <button className="btn btn-ghost btn-icon btn-sm" onClick={e => { e.stopPropagation(); onView(c.customerId); }} title="View">
          <Eye size={14} />
        </button>
      </div>
      <div className="cust-card-stats">
        <div className="cust-stat">
          <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--primary-dark)' }}>{c.totalOrders ?? 0}</span>
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Orders</span>
        </div>
        <div className="cust-stat">
          <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--red)' }}>♥ {c.wishlistCount ?? 0}</span>
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Wishlist</span>
        </div>
        <div className="cust-stat">
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', fontFamily: 'var(--mono)' }}>{maskPhone(c.phone)}</span>
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Phone</span>
        </div>
        <div className="cust-stat">
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>{c.memberSince ? new Date(c.memberSince).toLocaleDateString('en-IN') : '—'}</span>
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Joined</span>
        </div>
      </div>
    </button>
  );
}

CustomerCard.propTypes = {
  c: PropTypes.shape({
    customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fullName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    profilePicture: PropTypes.string,
    totalOrders: PropTypes.number,
    wishlistCount: PropTypes.number,
    memberSince: PropTypes.string,
  }).isRequired,
  onView: PropTypes.func.isRequired,
};

export default function Customers() {
  const [customers,    setCustomers]    = useState([]);
  const [page,         setPage]         = useState(0);
  const [totalPages,   setTotalPages]   = useState(0);
  const [total,        setTotal]        = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [loadingMore,  setLoadingMore]  = useState(false);
  const [search,       setSearch]       = useState('');
  const [selectedId,   setSelectedId]   = useState(null);
  const [isMobile,     setIsMobile]     = useState(window.innerWidth < 1024);
  const observerRef  = React.useRef(null);
  const sentinelRef  = React.useRef(null);
  const toast = useToast();

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // initial load / desktop page change
  const load = useCallback(async (pg = 0, append = false) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const res = await customersAPI.getAll(pg, 10);
      const content = res.data.content || [];
      setCustomers(prev => append ? [...prev, ...content] : content);
      setTotalPages(res.data.totalPages || 0);
      setTotal(res.data.totalElements || 0);
      setPage(pg);
    } catch {
      toast('Failed to load customers', 'error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(0); }, [load]);

  // desktop pagination
  const handlePageChange = (newPage) => { load(newPage); };

  // mobile lazy loading via IntersectionObserver
  useEffect(() => {
    if (!isMobile) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !loadingMore && page + 1 < totalPages) {
        load(page + 1, true);
      }
    }, { threshold: 0.1 });
    if (sentinelRef.current) observerRef.current.observe(sentinelRef.current);
    return () => observerRef.current?.disconnect();
  }, [isMobile, loadingMore, page, totalPages, load]);

  const filtered = (() => {
    if (!search) return customers;
    const query = search.toLowerCase();
    const words = query.split(/\s+/).filter(Boolean);
    return customers.map(c => {
      const name  = (c.fullName || '').toLowerCase();
      const email = (c.email || '').toLowerCase();
      const phone = (c.phone || '');
      let sc = 0;
      if (name === query)          sc += 1000;
      if (name.startsWith(query))  sc += 500;
      else if (words.every(w => name.includes(w))) sc += 400;
      if (email.startsWith(query)) sc += 300;
      if (phone.includes(query))   sc += 200;
      words.forEach(w => {
        if (name.includes(w))  sc += 50;
        if (email.includes(w)) sc += 30;
      });
      return { c, sc };
    }).filter(x => x.sc > 0)
      .sort((a, b) => b.sc - a.sc)
      .map(x => x.c);
  })();

  return (
    <Layout title="Customers" subtitle="View and manage registered customers">
      {/* Filter bar: search + refresh side by side */}
      <div className="filter-bar cust-filter-bar">
        <div className="search-box cust-search">
          <Search size={14} className="search-icon" />
          <input placeholder="Search by name, email, phone..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => load(0)}><RefreshCw size={13} /></button>
      </div>

      {/* Mobile: cards with lazy load */}
      {isMobile ? (
        <div className="cust-cards-list">
          {loading ? (
            <div className="page-loader"><div className="spinner" /></div>
          ) : filtered.length === 0 ? (
            <div className="empty-state"><h3>No customers found</h3></div>
          ) : (
            <>
              {filtered.map(c => (
                <CustomerCard key={c.customerId} c={c} onView={setSelectedId} />
              ))}
              {/* sentinel for lazy loading */}
              <div ref={sentinelRef} style={{ height: 1 }} />
              {loadingMore && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                  <div className="spinner" />
                </div>
              )}
              {page + 1 >= totalPages && customers.length > 0 && (
                <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)', padding: '12px 0' }}>
                  All {total} customers loaded
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        /* Desktop: table with pagination */
        <div className="card">
          <div className="table-wrap" style={{ overflowX: 'auto' }}>
            <table style={{ minWidth: 860, width: '100%' }}>
              <colgroup>
                <col style={{ width: 180 }} />
                <col style={{ width: 220 }} />
                <col style={{ width: 130 }} />
                <col style={{ width: 80 }} />
                <col style={{ width: 80 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 80 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Orders</th>
                  <th>Wishlist</th>
                  <th>Member Since</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: 'auto' }} /></td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={7}><div className="empty-state"><h3>No customers found</h3></div></td></tr>
                ) : filtered.map(c => (
                  <tr key={c.customerId}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div className="avatar">
                          {c.profilePicture
                            ? <img src={c.profilePicture} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                            : c.fullName?.[0]}
                        </div>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{c.fullName}</span>
                      </div>
                    </td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
                      <span style={{ whiteSpace: 'nowrap', display: 'inline-block' }}>{maskEmail(c.email)}</span>
                    </td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
                      <span style={{ whiteSpace: 'nowrap', display: 'inline-block' }}>{maskPhone(c.phone)}</span>
                    </td>
                    <td>
                      <span style={{ background: 'var(--primary-glow-sm)', color: 'var(--primary-dark)', borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>
                        {c.totalOrders ?? 0}
                      </span>
                    </td>
                    <td>
                      <span style={{ color: 'var(--red)', fontSize: 13 }}>♥ {c.wishlistCount ?? 0}</span>
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                      {c.memberSince ? new Date(c.memberSince).toLocaleDateString('en-IN') : '—'}
                    </td>
                    <td>
                      <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setSelectedId(c.customerId)} title="View">
                        <Eye size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <span>Showing {filtered.length} of {total} customers</span>
            <div className="pagination-controls">
              <button className="btn btn-ghost btn-sm" disabled={page === 0} onClick={() => handlePageChange(page - 1)}>← Prev</button>
              <span style={{ padding: '6px 12px', fontSize: 13 }}>Page {page + 1} of {Math.max(1, totalPages)}</span>
              <button className="btn btn-ghost btn-sm" disabled={page >= totalPages - 1} onClick={() => handlePageChange(page + 1)}>Next →</button>
            </div>
          </div>
        </div>
      )}

      {selectedId && <CustomerDetailModal customerId={selectedId} onClose={() => setSelectedId(null)} />}
    </Layout>
  );
}